import React from 'react';

import { Alert, Checkbox, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import {
  BulkPatchPropertyField,
  BulkPatchPropertyPreviewRow,
  BulkPatchPropertyValueChange,
} from '~/services/BedbankService';

const PATCH_FIELDS: Array<BulkPatchPropertyField> = ['status', 'domain', 'airport'];

function getRowKey(row: Pick<BulkPatchPropertyPreviewRow, 'id'>) {
  return row.id;
}

function renderPreviewValue(change?: BulkPatchPropertyValueChange) {
  if (!change) {
    return '-';
  }

  const oldValue = typeof change.oldValue === 'undefined' ? '' : (change.oldValue ?? 'null');
  const newValue = typeof change.newValue === 'undefined' ? '' : (change.newValue ?? 'null');
  return oldValue === newValue ? oldValue : `${oldValue} ⇒ ${newValue}`;
}

interface Props {
  rows: Array<BulkPatchPropertyPreviewRow>;
  selectedRowKeys: Set<string>;
  onToggleRow: (row: BulkPatchPropertyPreviewRow) => void;
  onToggleAllRows: () => void;
}

export default function BulkPropertyUpdatePreviewTable({ rows, selectedRowKeys, onToggleRow, onToggleAllRows }: Props) {
  const selectableRows = rows.filter((row) => row.action === 'update');

  if (rows.length === 0) {
    return <Alert severity="info">Run preview to see changes.</Alert>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              checked={selectableRows.length > 0 && selectedRowKeys.size === selectableRows.length}
              indeterminate={selectedRowKeys.size > 0 && selectedRowKeys.size < selectableRows.length}
              onChange={onToggleAllRows}
            />
          </TableCell>
          <TableCell>Property ID</TableCell>
          <TableCell>Action</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Domain</TableCell>
          <TableCell>Airport</TableCell>
          <TableCell>Error</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const isSelectable = row.action === 'update';
          return (
            <TableRow key={row.id} selected={selectedRowKeys.has(getRowKey(row))}>
              <TableCell padding="checkbox">
                <Checkbox
                  disabled={!isSelectable}
                  checked={selectedRowKeys.has(getRowKey(row))}
                  onChange={() => onToggleRow(row)}
                />
              </TableCell>
              <TableCell>{row.id}</TableCell>
              <TableCell>{row.action}</TableCell>
              {PATCH_FIELDS.map((field) => (
                <TableCell key={field}>{renderPreviewValue(row[field])}</TableCell>
              ))}
              <TableCell>{row.error ?? '-'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
